import { useEffect, useMemo, useState } from 'react';
import { first } from 'lodash';
import { isRemoteAndNotDisconnected } from 'App/Activity/components/Utility';
import { PowerState, SessionSupport } from 'App/Activity/Network/ActionsUtil';
import { isDesktopSession } from 'App/Activity/ResourceActionHandler';
import {
	DesktopAction,
	LaunchAction,
	MachineAction,
	MachineData,
	Session,
	SessionAction,
	SessionConnectionState,
	UNKNOWN_STATE,
	useResourceManagerContext,
} from 'App/Activity/ResourceManagerContext';
import { useTransitioningStatus } from 'App/Activity/useTransitioningStatus';
import { isSharedResource } from 'App/Tile/utils';
import { environment } from 'Environment';
import { FeatureFlag } from 'Environment/FeatureFlag';
import { useFeatureCanary, useFeatureCanaryConfig } from 'utils/useFeatureCanary';
import { Resource } from 'Workspace/ResourceProvider/resourceTypes';
import { useUserContext } from 'Workspace/UserContext';

interface SessionActionsProps {
	session?: Session;
	machine?: MachineData;
	resource?: Resource;
}

export const useDesktopActions = ({
	session,
	machine,
	resource,
}: SessionActionsProps) => {
	const [actions, setActions] = useState<DesktopAction[]>([]);
	const { deviceId } = useResourceManagerContext();
	const combinedDeviceId =
		environment?.nativeCapabilities?.platform?.deviceId || deviceId;
	const transitioningStatus = useTransitioningStatus(
		session || ({ machineData: machine } as Session)
	);
	const isPowerManagementControlsFeatureEnabled = useFeatureCanary(
		FeatureFlag.WSUIPowerManagementControls
	);

	const isGracefulRestartEnabled = useFeatureCanaryConfig(
		FeatureFlag.EnableGracefulRestart
	);

	const isGranularPowerActionEnabled = useFeatureCanary(
		FeatureFlag.EnableGranularPowerAction
	);
	const { powerManagementControls } = useUserContext();

	// Activity Manager passes only `session` (no `resource`), but the granular
	// power-action checks need resource-level admin flags (canrestart, canshutdown, etc.).
	// Fall back to the session's resource so those checks work on every surface.
	const resolvedResource = useMemo(
		() => resource || session?.applications?.[0]?.resource,
		[resource, session]
	);

	useEffect(() => {
		if (transitioningStatus === UNKNOWN_STATE) {
			const desktopActions: DesktopAction[] = [];
			if (canResume(machine)) {
				desktopActions.push(LaunchAction.RESUME);
			} else {
				canDisconnect(session) && desktopActions.push(SessionAction.DISCONNECT);
				canReconnect(session) && desktopActions.push(LaunchAction.RECONNECT);
				canLogout(session) && desktopActions.push(SessionAction.LOGOUT);

				if (isGracefulRestartEnabled && machine && !isSharedResource(resolvedResource)) {
					canControlledRestart(machine) &&
						isGranularRestartEnabled(resolvedResource, isGranularPowerActionEnabled) &&
						desktopActions.push(MachineAction.CONTROLLED_RESTART);
				} else {
					canPerformRestart(session, resolvedResource, isGranularPowerActionEnabled) &&
						desktopActions.push(LaunchAction.RESTART);
				}

				session &&
					canSessionHandoff(session, combinedDeviceId) &&
					desktopActions.push(LaunchAction.TRANSFER); // TODO: Need to be corrected as it will be true for null session
				canPerformMachineAction(
					MachineAction.SHUTDOWN,
					session,
					machine,
					resolvedResource,
					isGranularPowerActionEnabled
				) &&
					(!isPowerManagementControlsFeatureEnabled ||
						powerManagementControls?.shutdown) &&
					desktopActions.push(MachineAction.SHUTDOWN);
				canPerformMachineAction(
					MachineAction.POWEROFF,
					session,
					machine,
					resolvedResource,
					isGranularPowerActionEnabled
				) &&
					(!isPowerManagementControlsFeatureEnabled ||
						powerManagementControls?.powerOff) &&
					desktopActions.push(MachineAction.POWEROFF);
				canPerformMachineAction(
					MachineAction.HIBERNATE,
					session,
					machine,
					resolvedResource,
					isGranularPowerActionEnabled
				) &&
					(!isPowerManagementControlsFeatureEnabled ||
						powerManagementControls?.hibernate) &&
					desktopActions.push(MachineAction.HIBERNATE);
			}
			setActions(desktopActions);
		} else {
			setActions([]);
		}
	}, [
		transitioningStatus,
		session,
		machine,
		resolvedResource,
		deviceId,
		combinedDeviceId,
		isGracefulRestartEnabled,
		isGranularPowerActionEnabled,
		isPowerManagementControlsFeatureEnabled,
		powerManagementControls,
	]);

	return actions;
};

//allow-unused-export
export function canDisconnect(session: Session) {
	return session?.connectionState === SessionConnectionState.CONNECTED;
}

export function canReconnect(session: Session) {
	return (
		session?.connectionState === SessionConnectionState.DISCONNECTED &&
		session?.machineData?.powerState !== PowerState.SUSPENDED
	);
}

//allow-unused-export
export function canLogout(session: Session) {
	return (
		session?.connectionState === SessionConnectionState.CONNECTED ||
		(session?.logoffDisconnectedSupported &&
			session?.connectionState === SessionConnectionState.DISCONNECTED)
	);
}

const isMachinePoweredOff = (machine: MachineData) =>
	machine?.powerState === PowerState.OFF;

const canShutDown = (machine: MachineData) =>
	canPerformSingleSessionMachineAction(machine, 'shutDown');

const canPowerOff = (machine: MachineData) =>
	canPerformSingleSessionMachineAction(machine, 'turnOff');

function canPerformSingleSessionMachineAction(
	machine: MachineData,
	action: 'shutDown' | 'turnOff' | 'suspend'
) {
	const actionSupportMap = {
		shutDown: machine?.shutDownSupported,
		turnOff: machine?.turnOffSupported,
		suspend: machine?.suspendSupported,
	};
	const isMachineOn = !isMachinePoweredOff(machine);
	const isSingleSessionSupported =
		machine?.sessionSupport === SessionSupport.SINGLE_SESSION;
	const isActionSupported = actionSupportMap[action];

	return isMachineOn && isSingleSessionSupported && isActionSupported;
}
function canRestart(session: Session, resource?: Resource) {
	return (
		(isDesktopSession(session) &&
			!!first(session?.applications)?.resource?.poweroffurl) ||
		(!!resource?.isdesktop && !!resource?.poweroffurl)
	);
}

function canControlledRestart(machine: MachineData) {
	return canShutDown(machine) || canPowerOff(machine);
}

type GranularAction =
	| MachineAction.SHUTDOWN
	| MachineAction.POWEROFF
	| MachineAction.HIBERNATE;

const RESOURCE_FIELD_BY_ACTION: Record<
	GranularAction,
	'canshutdown' | 'canpoweroff' | 'cansuspend'
> = {
	[MachineAction.SHUTDOWN]: 'canshutdown',
	[MachineAction.POWEROFF]: 'canpoweroff',
	[MachineAction.HIBERNATE]: 'cansuspend',
};

export function isGranularRestartEnabled(
	resource: Resource | undefined,
	isGranularPowerActionEnabled: boolean
): boolean {
	return isGranularPowerActionEnabled ? !!resource?.canrestart : true;
}

function canPerformRestart(
	session: Session,
	resource: Resource | undefined,
	isGranularPowerActionEnabled: boolean
) {
	return (
		canRestart(session, resource) &&
		isGranularRestartEnabled(resource, isGranularPowerActionEnabled)
	);
}

function isMachineActionEnabledByAdmin(
	action: GranularAction,
	session: Session,
	resource: Resource,
	isGranularPowerActionEnabled: boolean
) {
	if (isGranularPowerActionEnabled) {
		return !!resource?.[RESOURCE_FIELD_BY_ACTION[action]];
	}
	// FF off: preserve legacy behavior — hibernate is ungated,
	// shutdown/poweroff require the resource's poweroffurl (via canRestart).
	return action === MachineAction.HIBERNATE || canRestart(session, resource);
}

function canPerformMachineAction(
	action: GranularAction,
	session: Session,
	machine: MachineData,
	resource: Resource,
	isGranularPowerActionEnabled: boolean
) {
	const machineActionToLegacy: Record<
		GranularAction,
		'shutDown' | 'turnOff' | 'suspend'
	> = {
		[MachineAction.SHUTDOWN]: 'shutDown',
		[MachineAction.POWEROFF]: 'turnOff',
		[MachineAction.HIBERNATE]: 'suspend',
	};
	return (
		isMachineActionEnabledByAdmin(
			action,
			session,
			resource,
			isGranularPowerActionEnabled
		) && canPerformSingleSessionMachineAction(machine, machineActionToLegacy[action])
	);
}

function canResume(machine: MachineData) {
	return machine?.powerState === PowerState.SUSPENDED;
}

function canSessionHandoff(session: Session, deviceId: string) {
	return isRemoteAndNotDisconnected(session, deviceId);
}
